import { Injector } from '@angular/core';
import type { Firestore } from 'firebase/firestore';
import { getAnalytics, isSupported, logEvent, Analytics } from 'firebase/analytics';
import { onCLS, onINP, onLCP, onTTFB, Metric } from 'web-vitals';
import { environment } from '../../../environments/environment';
import { FirebaseLoaderService } from 'src/app/services/firebase-loader.service';

type MeasurementUnit = 'millisecond' | 'ratio';

let webVitalsInitialized = false;

interface WebVitalPayload {
  metric_name: string;
  metric_value: number;
  metric_unit: MeasurementUnit;
  route: string;
  app_version: string;
  environment: string;
  device: string;
}

function measurementUnit(name: string): MeasurementUnit {
  return name === 'CLS' ? 'ratio' : 'millisecond';
}

function shouldSample(): boolean {
  const sampleRate = environment.webVitalsSampleRate ?? 1;
  return Math.random() <= sampleRate;
}

function resolveDevice(): string {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  if (nav.userAgentData?.platform) {
    return nav.userAgentData.platform;
  }
  if (navigator.platform) {
    return navigator.platform;
  }
  return 'unknown';
}

function buildPayload(metric: Metric): WebVitalPayload {
  return {
    metric_name: metric.name,
    metric_value: metric.value,
    metric_unit: measurementUnit(metric.name),
    route: window.location.pathname,
    app_version: environment.sentryRelease ?? 'unknown',
    environment: environment.sentryEnvironment ?? 'unknown',
    device: resolveDevice(),
  };
}

async function resolveAnalytics(): Promise<Analytics | null> {
  if (!environment.enableWebVitalsTracking) {
    return null;
  }
  const supported = await isSupported();
  if (!supported) {
    return null;
  }
  return getAnalytics();
}

async function sendWebVital(metric: Metric, firestore: Firestore | null, analytics: Analytics | null): Promise<void> {
  const payload = buildPayload(metric);

  if (analytics) {
    logEvent(analytics, 'web_vital', payload);
    return;
  }

  if (firestore) {
    const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
    const vitalsCollection = collection(firestore, 'performance_web_vitals');
    await addDoc(vitalsCollection, {
      timestamp: serverTimestamp(),
      metric: payload.metric_name,
      value: payload.metric_value,
      unit: payload.metric_unit,
      environment: payload.environment,
      appVersion: payload.app_version,
      route: payload.route,
      device: payload.device,
    });
  }
}

export async function initWebVitalsFirebase(injector: Injector): Promise<void> {
  if (webVitalsInitialized || typeof window === 'undefined') {
    return;
  }
  if (!environment.enableWebVitalsTracking) {
    return;
  }
  if (!shouldSample()) {
    return;
  }

  webVitalsInitialized = true;

  const firebaseLoader = injector.get(FirebaseLoaderService, null);
  const firestore = firebaseLoader ? await firebaseLoader.getFirestore().catch(() => null) : null;
  const analytics = await resolveAnalytics();

  const handleMetric = (metric: Metric) => {
    void sendWebVital(metric, firestore, analytics);
  };

  onCLS(handleMetric);
  onLCP(handleMetric);
  onINP(handleMetric);
  onTTFB(handleMetric);
}
